import React, { useCallback, useEffect, useState } from "react";
import uiConfig from "../config/uiConfig";

import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Snackbar,
  Alert,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";

import RoleTable from "../components/roles/RoleTable";
import RoleDialog from "../components/roles/RoleDialog";

import { getRoles, createRole, updateRole, deleteRole } from "../services/roleService";
import { setDynamicPageAccess } from "../auth/roles";

function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const loadRoles = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getRoles();
      setRoles(data);
      // Keep the app's live permission map in sync with any changes made here.
      setDynamicPageAccess(data);

      setSelectedRoleIds((prev) => {
        const validIds = new Set(data.filter((role) => !role.is_system).map((role) => role.role_id));
        return prev.filter((id) => validIds.has(id));
      });
    } catch (error) {
      console.error("Role Load Error:", error);
      setSnackbar({ open: true, message: "Failed to load roles", severity: "error" });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  const filteredRoles = roles.filter((role) =>
    role.role_name.toLowerCase().includes(search.toLowerCase())
  );

  const handleSave = async (data) => {
    if (isSaving) {
      return;
    }

    try {
      setIsSaving(true);

      if (selectedRole) {
        await updateRole(selectedRole.role_id, data);
      } else {
        await createRole(data);
      }

      setDialogOpen(false);
      setSelectedRole(null);

      await loadRoles();

      setSnackbar({ open: true, message: "Role saved successfully", severity: "success" });
    } catch (error) {
      console.error("Role Save Error:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to save role",
        severity: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole || isDeleting) {
      return;
    }

    try {
      setIsDeleting(true);
      await deleteRole(selectedRole.role_id);

      setDeleteOpen(false);
      setSelectedRole(null);

      await loadRoles();

      setSnackbar({ open: true, message: "Role deleted successfully", severity: "success" });
    } catch (error) {
      console.error("Role Delete Error:", error);
      setSnackbar({
        open: true,
        message: error?.response?.data?.detail || "Failed to delete role",
        severity: "error"
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRoleIds.length === 0 || isBulkDeleting) {
      return;
    }

    try {
      setIsBulkDeleting(true);

      let successCount = 0;
      let failedCount = 0;

      for (const roleId of selectedRoleIds) {
        try {
          await deleteRole(roleId);
          successCount += 1;
        } catch (error) {
          failedCount += 1;
        }
      }

      setBulkDeleteOpen(false);
      setSelectedRoleIds([]);

      await loadRoles();

      if (failedCount === 0) {
        setSnackbar({
          open: true,
          message: `${successCount} role(s) deleted successfully`,
          severity: "success"
        });
      } else {
        setSnackbar({
          open: true,
          message: `${successCount} deleted, ${failedCount} failed`,
          severity: "warning"
        });
      }
    } catch (error) {
      console.error("Role Bulk Delete Error:", error);
      setBulkDeleteOpen(false);
      setSnackbar({ open: true, message: "Bulk delete failed", severity: "error" });
    } finally {
      setIsBulkDeleting(false);
    }
  };

  return (
    <Box sx={{ bgcolor: "background.default", minHeight: "100vh", p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" fontWeight={600} color="text.primary">
            Role Management
          </Typography>

          <Typography sx={{ fontSize: 13, color: "text.secondary" }}>
            Create custom roles and control which pages each role can access.
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={1.2}>
          <Button
            variant="outlined"
            color="error"
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
            onClick={() => setBulkDeleteOpen(true)}
            disabled={selectedRoleIds.length === 0 || isBulkDeleting}
            sx={{
              height: 38,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            {isBulkDeleting ? "Deleting..." : `Delete All (${selectedRoleIds.length})`}
          </Button>

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedRole(null);
              setDialogOpen(true);
            }}
            sx={{
              height: 38,
              background: uiConfig.buttonColor,
              textTransform: "none",
              borderRadius: 2,
              px: 2,
            }}
          >
            Add Role
          </Button>
        </Box>
      </Box>

      <Box
        sx={{
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          p: 1.5,
          mb: 2,
        }}
      >
        <TextField
          fullWidth
          size="small"
          placeholder="Search role name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 20, color: "text.secondary" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            "& .MuiInputBase-root": {
              height: 38,
            },
          }}
        />
      </Box>

      <RoleTable
        roles={filteredRoles}
        loading={loading}
        selectedIds={selectedRoleIds}
        onSelectionChange={setSelectedRoleIds}
        onEdit={(role) => {
          setSelectedRole(role);
          setDialogOpen(true);
        }}
        onDelete={(role) => {
          setSelectedRole(role);
          setDeleteOpen(true);
        }}
      />

      <RoleDialog
        open={dialogOpen}
        role={selectedRole}
        isSaving={isSaving}
        onClose={() => {
          if (isSaving) {
            return;
          }
          setDialogOpen(false);
          setSelectedRole(null);
        }}
        onSave={handleSave}
      />

      <Dialog
        open={deleteOpen}
        onClose={() => {
          if (!isDeleting) {
            setDeleteOpen(false);
            setSelectedRole(null);
          }
        }}
      >
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the role{" "}
            <strong>{selectedRole?.role_name?.replace(/_/g, " ")}</strong>? Employees currently
            assigned this role will keep the value but lose page access until reassigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteOpen(false)} disabled={isDeleting}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteOpen}
        onClose={() => {
          if (isBulkDeleting) {
            return;
          }
          setBulkDeleteOpen(false);
        }}
        disableEscapeKeyDown={isBulkDeleting}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Selected Roles</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all selected roles?
            <br />
            <br />
            Selected: <strong>{selectedRoleIds.length}</strong>
            <br />
            Employees currently assigned a deleted role will keep the value but lose page access
            until reassigned.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => setBulkDeleteOpen(false)}
            disabled={isBulkDeleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleBulkDelete}
            disabled={isBulkDeleting}
            startIcon={
              isBulkDeleting
                ? <CircularProgress size={16} color="inherit" />
                : <DeleteSweepIcon />
            }
          >
            {isBulkDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default RoleManagement;
